import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    DEFAULT_THEME_ASSETS,
    THEME_ASSET_CSS_VARS,
    type ThemeAssets,
} from './theme.assets.defaults';
import { DEFAULT_EXTENDED_CSS_VARS } from './theme.extended.defaults';
import {
    DEFAULT_FULL_THEME,
    FULL_THEME_CSS_VARS,
    type FullThemeTokens,
} from './theme.full';

/**
 * Punto de entrada único del tema en runtime.
 * Aplica tokens como CSS custom properties en `:root` para que SCSS, HTML y TS los consuman.
 */
@Injectable({
    providedIn: 'root',
})
export class AppThemeService {
    private readonly document = inject(DOCUMENT);
    private readonly tokensSubject = new BehaviorSubject<FullThemeTokens>({
        ...DEFAULT_FULL_THEME,
    });
    private readonly assetsSubject = new BehaviorSubject<ThemeAssets>({
        ...DEFAULT_THEME_ASSETS,
    });
    private extendedCssVars: Record<string, string> = { ...DEFAULT_EXTENDED_CSS_VARS };
    /** Bumps whenever login background URL changes so stale onerror handlers are ignored. */
    private loginBackgroundLoadToken = 0;

    readonly tokens$ = this.tokensSubject.asObservable();
    readonly assets$ = this.assetsSubject.asObservable();

    get snapshot(): FullThemeTokens {
        return this.tokensSubject.value;
    }

    get assetsSnapshot(): ThemeAssets {
        return this.assetsSubject.value;
    }

    /** Snapshot of extended CSS vars currently applied on `:root`. */
    getExtendedCssVarsSnapshot(): Record<string, string> {
        return { ...this.extendedCssVars };
    }

    /** Aplica overrides parciales sobre los defaults y los publica en `:root`. */
    applyTokens(
        overrides: Partial<FullThemeTokens> = {},
        cssVarOverrides: Record<string, string> = {},
        assetOverrides: Partial<ThemeAssets> = {},
    ): void {
        const next: FullThemeTokens = {
            ...DEFAULT_FULL_THEME,
            ...overrides,
        };
        this.tokensSubject.next(next);
        this.extendedCssVars = {
            ...DEFAULT_EXTENDED_CSS_VARS,
            ...cssVarOverrides,
        };
        this.writeCssVariables(next);
        this.writeExtendedCssVariables(this.extendedCssVars);
        this.applyAssets(assetOverrides);
    }

    /** Aplica assets de tema (logos, fondo de login, etc.). */
    applyAssets(overrides: Partial<ThemeAssets> = {}): void {
        const next: ThemeAssets = {
            ...DEFAULT_THEME_ASSETS,
            ...overrides,
        };
        this.assetsSubject.next(next);
        this.writeAssetCssVariables(next);
        this.ensureLoginBackgroundOrFallback(next.LOGIN_BACKGROUND_URL);
    }

    reset(): void {
        this.applyTokens({}, {}, {});
    }

    /** Lee un token resuelto (valor hex/rgb) desde `:root` — útil para APIs que no aceptan CSS vars. */
    getToken<K extends keyof FullThemeTokens>(key: K): string {
        const cssVar = FULL_THEME_CSS_VARS[key];
        const computed = getComputedStyle(this.document.documentElement)
            .getPropertyValue(cssVar)
            .trim();
        return computed || this.snapshot[key];
    }

    /** Lee una CSS var extendida resuelta. */
    getCssVarValue(cssVarName: string): string {
        const computed = getComputedStyle(this.document.documentElement)
            .getPropertyValue(cssVarName)
            .trim();
        return computed || this.extendedCssVars[cssVarName] || '';
    }

    getAsset<K extends keyof ThemeAssets>(key: K): string {
        return this.assetsSnapshot[key];
    }

    /** Referencia CSS var para bindings inline: `var(--color-primary)` */
    cssVar<K extends keyof FullThemeTokens>(key: K): string {
        return `var(${FULL_THEME_CSS_VARS[key]})`;
    }

    private writeCssVariables(tokens: FullThemeTokens): void {
        const root = this.document.documentElement;
        (Object.keys(FULL_THEME_CSS_VARS) as Array<keyof FullThemeTokens>).forEach((key) => {
            root.style.setProperty(FULL_THEME_CSS_VARS[key], tokens[key]);
        });
    }

    private writeExtendedCssVariables(cssVars: Record<string, string>): void {
        const root = this.document.documentElement;
        Object.entries(cssVars).forEach(([cssVar, value]) => {
            root.style.setProperty(cssVar, value);
        });
    }

    private writeAssetCssVariables(assets: ThemeAssets): void {
        const root = this.document.documentElement;
        root.style.setProperty(
            THEME_ASSET_CSS_VARS.LOGIN_BACKGROUND_URL,
            `url("${assets.LOGIN_BACKGROUND_URL}")`,
        );
        root.style.setProperty(THEME_ASSET_CSS_VARS.LOGO_URL, `url("${assets.LOGO_URL}")`);
    }

    /**
     * If the themed login background cannot be fetched, restore the app default
     * (CSS also stacks a fallback layer for paint while loading / on 404).
     */
    private ensureLoginBackgroundOrFallback(url: string): void {
        const defaultUrl = DEFAULT_THEME_ASSETS.LOGIN_BACKGROUND_URL;
        const token = ++this.loginBackgroundLoadToken;
        if (!url || url === defaultUrl) {
            return;
        }

        const probe = new Image();
        probe.onload = () => {
            /* keep themed background */
        };
        probe.onerror = () => {
            if (token !== this.loginBackgroundLoadToken) {
                return;
            }
            const restored: ThemeAssets = {
                ...this.assetsSubject.value,
                LOGIN_BACKGROUND_URL: defaultUrl,
            };
            this.assetsSubject.next(restored);
            this.writeAssetCssVariables(restored);
        };
        probe.src = url;
    }
}
